import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogIn, ShieldCheck } from "lucide-react";
import { fetchMe, passwordLogin } from "@/lib/api";
import { toast } from "sonner";

const AdminLogin = () => {
    const navigate = useNavigate();
    const [checking, setChecking] = useState(true);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let alive = true;
        fetchMe()
            .then((user) => { if (alive) navigate("/admin", { replace: true, state: { user } }); })
            .catch(() => { if (alive) setChecking(false); });
        return () => { alive = false; };
    }, [navigate]);

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim() || !password) return;
        setSubmitting(true);
        try {
            await passwordLogin(email.trim(), password);
            toast.success("Signed in.");
            navigate("/admin", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invalid email or password.");
        } finally {
            setSubmitting(false);
        }
    };

    if (checking) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 size={28} className="animate-spin text-maroon-600" />
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-md px-4 sm:px-6 lg:px-8 py-16">
            <div className="rounded-2xl border border-maroon-100 bg-white/85 p-8 shadow-sm text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-maroon-600 text-cream-50">
                    <ShieldCheck size={26} />
                </div>
                <h1 className="font-serif text-3xl text-maroon-800 mt-5">Parish Admin</h1>
                <div className="gold-divider my-4 mx-auto max-w-[160px] opacity-60" />

                <p className="text-sm text-maroon-700/85">
                    Sign in with your admin email and password.
                </p>
                <form onSubmit={handlePasswordSubmit} className="mt-4 space-y-3 text-left">
                    <label className="block text-xs font-semibold text-maroon-800">Email
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            required
                            data-testid="admin-password-email"
                            className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                    </label>
                    <label className="block text-xs font-semibold text-maroon-800">Password
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                            data-testid="admin-password-input"
                            className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="admin-password-submit"
                        className="btn-primary w-full justify-center"
                    >
                        {submitting ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />} Sign in
                    </button>
                </form>

                <p className="text-[11px] text-maroon-700/60 italic mt-4">
                    This page is not linked from public pages. Bookmark it for quick access.
                </p>
            </div>
        </div>
    );
};

export default AdminLogin;
